import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";

// Category Theme Colors
const CATEGORY_COLORS = {
  supplies: "#F97316",     // Orange
  manpower: "#22C55E",     // Green
  compliance: "#3B82F6",   // Blue
  it: "#7C3AED",           // Purple
};

// Direct Service Navigation with Visual Category Grouping
const CATEGORY_MENUS = {
  supplies: {
    label: "Supplies",
    icon: "📦",
    color: CATEGORY_COLORS.supplies,
    services: [
      { label: "Office Supplies", slug: "office-supplies" },
      { label: "Kitchen Pantry", slug: "kitchen-pantry" },
      { label: "Fresh Fruits", slug: "fresh-fruits" },
      { label: "Branded Apparel", slug: "branded-apparel" },
      { label: "Corporate Gifts", slug: "corporate-gifts" },
      { label: "Organic Canteen", slug: "organic-canteen" },
    ],
  },
  manpower: {
    label: "Manpower",
    icon: "👨‍💼",
    color: CATEGORY_COLORS.manpower,
    services: [
      { label: "Permanent Staffing", slug: "permanent-staffing" },
      { label: "Temporary Staffing", slug: "temporary-staffing" },
      { label: "Contract Staffing", slug: "contract-staffing" },
      { label: "Skilled Manpower", slug: "skilled-manpower" },
      { label: "Unskilled Labour", slug: "unskilled-labour" },
      { label: "Executive Search", slug: "executive-search" },
      { label: "Facility Management", slug: "facility-management" },
      { label: "Payroll Management", slug: "payroll-management" },
    ],
  },
  compliance: {
    label: "Compliance",
    icon: "🧾",
    color: CATEGORY_COLORS.compliance,
    services: [
      { label: "Accounting Services", slug: "accounting" },
      { label: "GST Filing", slug: "gst-filing" },
      { label: "Income Tax", slug: "income-tax" },
      { label: "MCA Compliance", slug: "mca-compliance" },
    ],
  },
  it: {
    label: "IT Services",
    icon: "💻",
    color: CATEGORY_COLORS.it,
    services: [
      { label: "Managed IT", slug: "managed-it" },
      { label: "Cybersecurity", slug: "cybersecurity" },
      { label: "Cloud Computing", slug: "cloud-computing" },
      { label: "IT Consulting", slug: "it-consulting" },
      { label: "Help Desk Support", slug: "help-desk" },
    ],
  },
};

export function Navbar({ onOpenAppointment, themeColor, activeCategory }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileAccordions, setMobileAccordions] = useState({});
  const dropdownRefs = useRef({});
  const navigate = useNavigate();
  
  // Default to red if no theme color is provided (for homepage)
  const logoColor = themeColor || "#dc2626";
  const ctaColor = themeColor || "#dc2626";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setMobileAccordions({});
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown]) {
        if (!dropdownRefs.current[activeDropdown].contains(e.target)) {
          setActiveDropdown(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  const handleServiceClick = (category, slug) => {
    navigate(`/${slug}`);
    setActiveDropdown(null);
    setMobileOpen(false);
    window.scrollTo(0, 0);
  };

  // Removed handleCategoryClick - no longer needed

  const toggleMobileAccordion = (category) => {
    setMobileAccordions((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-200"
          : "bg-white/80 backdrop-blur-lg"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 no-underline"
          onClick={() => window.scrollTo(0, 0)}
        >
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300"
            style={{ 
              background: `linear-gradient(to bottom right, ${logoColor}, ${logoColor}dd)`,
              boxShadow: `0 10px 15px -3px ${logoColor}30`
            }}
          >
            <span className="text-white font-black text-xl">S</span>
          </div>
          <div>
            <p className="font-black text-slate-900 text-base leading-none font-body">
              SwitchYourVendor
            </p>
            <p className="text-[10px] text-slate-400 font-semibold mt-0.5 font-body">
              Stop Settling. Start Switching.
            </p>
          </div>
        </Link>

        {/* Desktop Navigation - Direct Categories */}
        <div className="hidden lg:flex items-center gap-1">
          {Object.entries(CATEGORY_MENUS).map(([key, category]) => (
            <div
              key={key}
              className="relative"
              ref={(el) => (dropdownRefs.current[key] = el)}
            >
              <button
                data-testid={`nav-${key}`}
                onMouseEnter={() => setActiveDropdown(key)}
                onClick={() => setActiveDropdown(activeDropdown === key ? null : key)}
                className={`flex items-center gap-1 font-bold text-[13px] px-3 py-2 rounded-lg transition-all font-body ${
                  activeCategory === key 
                    ? 'text-slate-900' 
                    : 'text-slate-500 hover:text-slate-900'
                }`}
                style={activeCategory === key ? { color: category.color } : {}}
              >
                <span className="text-base mr-1">{category.icon}</span>
                {category.label}
                <ChevronDown
                  size={13}
                  className={`transition-transform duration-200 ${
                    activeDropdown === key ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {activeDropdown === key && (
                <div
                  onMouseLeave={() => setActiveDropdown(null)}
                  className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl py-2 min-w-[240px] z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  {/* Individual Services - Direct list without category header */}
                  <div className="py-1">
                    {category.services.map((service) => (
                      <button
                        key={service.slug}
                        data-testid={`dropdown-${key}-${service.slug}`}
                        onClick={() => handleServiceClick(key, service.slug)}
                        className="w-full text-left px-4 py-2 text-[13px] font-semibold text-slate-600 transition-all group font-body flex items-center gap-2 hover:bg-opacity-10"
                        style={{
                          '--hover-color': category.color,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${category.color}10`;
                          e.currentTarget.style.color = category.color;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#64748b';
                        }}
                      >
                        <ChevronRight
                          size={12}
                          className="text-slate-400 group-hover:opacity-100 transition-all"
                          style={{ color: 'inherit' }}
                        />
                        {service.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Book Appointment CTA */}
          <button
            data-testid="nav-book-appointment-btn"
            onClick={onOpenAppointment}
            className="ml-2 text-white font-extrabold text-[13px] px-5 py-2.5 rounded-full shadow-lg transition-all active:scale-95 font-body"
            style={{
              background: `linear-gradient(to right, ${ctaColor}, ${ctaColor}dd)`,
              boxShadow: `0 10px 15px -3px ${ctaColor}30`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 10px 15px -3px ${ctaColor}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = `0 10px 15px -3px ${ctaColor}30`;
            }}
          >
            Book Your Appointment
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-2 text-slate-600"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 shadow-xl max-h-[80vh] overflow-y-auto"
        >
          <div className="px-6 py-4 space-y-2">
            {/* Mobile Category Accordions */}
            {Object.entries(CATEGORY_MENUS).map(([key, category]) => (
              <div key={key} className="border-b border-slate-100 pb-2">
                {/* Category Header */}
                <button
                  onClick={() => toggleMobileAccordion(key)}
                  className="w-full flex items-center justify-between py-3 text-left"
                  data-testid={`mobile-accordion-${key}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{category.icon}</span>
                    <span className="font-extrabold text-sm text-slate-700 font-body">
                      {category.label}
                    </span>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-slate-400 transition-transform duration-200 ${
                      mobileAccordions[key] ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Accordion Content */}
                {mobileAccordions[key] && (
                  <div className="space-y-1 pl-4 animate-in slide-in-from-top-2 duration-200">
                    {/* Individual Services */}
                    {category.services.map((service) => (
                      <button
                        key={service.slug}
                        data-testid={`mobile-nav-${key}-${service.slug}`}
                        onClick={() => handleServiceClick(key, service.slug)}
                        className="w-full text-left px-3 py-2 text-sm font-semibold text-slate-600 rounded-lg transition-all font-body"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${category.color}10`;
                          e.currentTarget.style.color = category.color;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#64748b';
                        }}
                      >
                        {service.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile Book Appointment Button */}
            <button
              data-testid="mobile-book-appointment-btn"
              onClick={onOpenAppointment}
              className="w-full mt-4 text-white font-extrabold text-sm py-3 rounded-full shadow-lg font-body transition-all"
              style={{
                background: `linear-gradient(to right, ${ctaColor}, ${ctaColor}dd)`,
                boxShadow: `0 10px 15px -3px ${ctaColor}30`,
              }}
            >
              Book Your Appointment
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
